import React from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Circle,
  Flex,
  Badge,
  Tooltip
} from "@chakra-ui/react";
import {
  FaPlus,
  FaUser,
  FaPlayCircle,
  FaCheckCircle,
  FaTimesCircle,
  FaClock,
  FaEdit,
  FaComments,
  FaBuilding
} from "react-icons/fa";

const TicketTimeline = ({ ticket }) => {
  const borderColor = "gray.200";
  const textColor = "gray.800";
  const mutedTextColor = "gray.600";
  const cardBg = "white";
  const timelineBg = "gray.100";

  const statusColors = {
    'open': 'blue.500',
    'in_progress': 'yellow.500',
    'resolved': 'green.500',
    'closed': 'gray.500'
  };
  

  // Timeline events based on ticket data
  const getTimelineEvents = () => {
    const events = [];
    
    // Ticket created
    events.push({
      id: 'created',
      title: 'Ticket Oluşturuldu',
      description: `${ticket.full_name || 'Bilinmeyen kullanıcı'} tarafından oluşturuldu`,
      timestamp: ticket.created,
      icon: FaPlus,
      color: 'blue.500',
      bgColor: 'blue.50'
    });

    // Ticket assigned
    if (ticket.assigned_to) {
      events.push({
        id: 'assigned',
        title: 'Ticket Atandı',
        description: `${ticket.assigned_to.name} bayisine atandı`,
        timestamp: ticket.updated, // Assuming assignment happened on last update
        icon: FaUser,
        color: 'purple.500',
        bgColor: 'purple.50'
      });
    }

    // Unit assigned
    if (ticket.related_unit) {
      events.push({
        id: 'unit_assigned',
        title: 'Birim Atandı',
        description: `${ticket.related_unit.name} birimine atandı`,
        timestamp: ticket.updated,
        icon: FaBuilding,
        color: 'orange.500',
        bgColor: 'orange.50'
      });
    }

    // Status changes based on current state
    switch (ticket.state) {
      case 'in_progress':
        events.push({
          id: 'started',
          title: 'Ticket Başlatıldı',
          description: 'İş emri işleme alındı',
          timestamp: ticket.updated,
          icon: FaPlayCircle,
          color: 'green.500',
          bgColor: 'green.50'
        });
        break;
      case 'completed':
        events.push({
          id: 'completed',
          title: 'Ticket Tamamlandı',
          description: 'İş emri başarıyla tamamlandı',
          timestamp: ticket.updated,
          icon: FaCheckCircle,
          color: 'green.500',
          bgColor: 'green.50'
        });
        break;
      case 'cancelled':
        events.push({
          id: 'cancelled',
          title: 'Ticket İptal Edildi',
          description: 'İş emri iptal edildi',
          timestamp: ticket.updated,
          icon: FaTimesCircle,
          color: 'red.500',
          bgColor: 'red.50'
        });
        break;
    }

    // Sort events by timestamp
    return events.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  };

  const timelineEvents = getTimelineEvents();

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('tr-TR', {
        day: 'numeric',
        month: 'short'
      }),
      time: date.toLocaleTimeString('tr-TR', {
        hour: '2-digit',
        minute: '2-digit'
      })
    };
  };

  return (
    <VStack spacing={0} align="stretch">
      {timelineEvents.map((event, index) => {
        const { date, time } = formatDate(event.timestamp);
        const isLast = index === timelineEvents.length - 1;
        
        return (
          <Box key={event.id} position="relative">
            <HStack spacing={4} align="start" py={4}>
              {/* Timeline Line and Icon */}
              <Box position="relative" zIndex={2}>
                <Circle
                  size={10}
                  bg={event.bgColor}
                  border="2px solid"
                  borderColor={event.color}
                >
                  <Icon as={event.icon} color={event.color} boxSize={4} />
                </Circle>
                
                {/* Vertical Line */}
                {!isLast && (
                  <Box
                    position="absolute"
                    left="50%"
                    top="40px"
                    width="2px"
                    height="calc(100% + 16px)"
                    bg={borderColor}
                    transform="translateX(-50%)"
                    zIndex={1}
                  />
                )}
              </Box>

              {/* Event Content */}
              <Box flex={1} pb={!isLast ? 4 : 0}>
                <Flex justify="space-between" align="start" mb={2}>
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="bold" color={textColor}>
                      {event.title}
                    </Text>
                    <Text fontSize="xs" color={mutedTextColor}>
                      {event.description}
                    </Text>
                  </VStack>
                  
                  <VStack align="end" spacing={0}>
                    <Text fontSize="xs" fontWeight="semibold" color={event.color}>
                      {date}
                    </Text>
                    <Text fontSize="xs" color={mutedTextColor}>
                      {time}
                    </Text>
                  </VStack>
                </Flex>
              </Box>
            </HStack>
          </Box>
        );
      })}
      
      {/* Current Status Indicator */}
      <Box mt={4} p={3} bg={timelineBg} borderRadius="md">
        <HStack justify="space-between">
          <HStack spacing={2}>
            <Icon as={FaClock} color={mutedTextColor} boxSize={4} />
            <Text fontSize="sm" color={mutedTextColor}>Mevcut Durum:</Text>
          </HStack>
          <Badge 
            colorScheme={
              ticket.state === 'completed' ? 'green' :
              ticket.state === 'in_progress' ? 'orange' :
              ticket.state === 'cancelled' ? 'red' : 'blue'
            }
            variant="subtle"
          >
            {ticket.state === 'new' && 'Yeni'}
            {ticket.state === 'assigned' && 'Atandı'}
            {ticket.state === 'in_progress' && 'Devam Ediyor'}
            {ticket.state === 'completed' && 'Tamamlandı'}
            {ticket.state === 'cancelled' && 'İptal Edildi'}
          </Badge>
        </HStack>
      </Box>
    </VStack>
  );
};

export default TicketTimeline;